import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { Link } from "react-router-dom";
import { ShieldCheck, UserPlus, Check, Trash2, Clock, Loader2, Mail, Sparkles, FileSpreadsheet, KeyRound, Megaphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError, API, authToken } from "@/lib/api";
import { toast } from "sonner";

export default function AdminAccess() {
  const { user } = useApp();
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [approved, setApproved] = useState(0);
  const [inviteOnly, setInviteOnly] = useState(true);
  const [filter, setFilter] = useState(""); // "" | pending | approved
  const [newEmail, setNewEmail] = useState("");
  const [bulk, setBulk] = useState("");
  const [vipEmail, setVipEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviters, setInviters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pw, setPw] = useState({ open: false, current: "", next: "", confirm: "", busy: false });

  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match."); return; }
    setPw((p) => ({ ...p, busy: true }));
    try {
      const { data } = await api.post("/admin/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(data.message || "Password updated.");
      setPw({ open: false, current: "", next: "", confirm: "", busy: false });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setPw((p) => ({ ...p, busy: false }));
    }
  };

  const loadPendingUsers = useCallback(async () => {
    try { const { data } = await api.get("/admin/pending-users"); setPendingUsers(data.items || []); }
    catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/access${filter ? `?status=${filter}` : ""}`);
      setItems(data.items || []);
      setPending(data.pending || 0);
      setApproved(data.approved || 0);
      setInviteOnly(!!data.invite_only);
      try { const iv = await api.get("/admin/access/inviters"); setInviters(iv.data.inviters || []); } catch { /* noop */ }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (user?.role === "admin") { load(); loadPendingUsers(); } }, [user, load, loadPendingUsers]);

  const addEmail = async () => {
    if (!newEmail.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/access/add", { email: newEmail.trim() });
      toast.success(`Invited ${newEmail.trim()}`);
      setNewEmail(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addBulk = async () => {
    if (!bulk.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/access/bulk", { emails: bulk });
      toast.success(`Invited ${data.added} member${data.added === 1 ? "" : "s"}${data.invalid ? ` · ${data.invalid} skipped` : ""}`);
      setBulk(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendVip = async () => {
    if (!vipEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vip-invite", { email: vipEmail.trim() });
      toast.success(data.email_sent ? `VIP invitation sent to ${data.email} 🙏` : `Approved ${data.email} — email will send once your domain is verified`);
      setVipEmail(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approve = async (email) => {
    try { await api.post("/admin/access/approve", { email }); toast.success(`Approved ${email}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (email) => {
    try { await api.post("/admin/access/remove", { email }); toast.success(`Removed ${email}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const approveUser = async (u) => {
    try { await api.post("/admin/users/approve", { user_id: u.user_id }); toast.success(`Approved ${u.name || u.email}`); loadPendingUsers(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rejectUser = async (u) => {
    try { await api.post("/admin/users/reject", { user_id: u.user_id }); toast.success(`Rejected ${u.name || u.email}`); loadPendingUsers(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (user === undefined) return <div className="min-h-screen grid place-items-center text-sec"><HeartLoader /></div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center" data-testid="access-admin-denied">
          <ShieldCheck size={40} className="mx-auto text-brand mb-3" />
          <h1 className="font-serif text-2xl">Admins only</h1>
          <p className="text-sec text-sm mt-2">This page manages the invite list. <Link to="/" className="text-brand hover:underline">Go home</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-access-page">
      <div className="flex items-center gap-2 text-brand mb-1"><ShieldCheck size={18} /> <span className="text-xs uppercase tracking-widest">Launch control</span></div>
      <h1 className="font-serif text-3xl tracking-tight">Member Approvals</h1>
      <p className="text-sec text-sm mt-1">Signups are <span className="text-emerald-400">open</span> — new members can sign in with Google right away, and wait here for your welcome-approval to unlock full access.</p>

      <Link to="/admin/marketing" data-testid="open-marketing-console" className="mt-4 inline-flex items-center gap-2 btn-primary rounded-full px-5 h-10 text-sm">
        <Megaphone size={15} /> Open Email Marketing Console
      </Link>

      <button data-testid="download-pricing-matrix" onClick={async () => {
        try {
          const res = await fetch(`${API}/admin/pricing-matrix.xlsx`, { headers: { Authorization: `Bearer ${authToken.get()}` }, credentials: "include" });
          if (!res.ok) throw new Error();
          const blob = await res.blob();
          const href = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = href; a.download = "my-kind-pricing.xlsx";
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
        } catch { toast.error("Couldn't download — admins only."); }
      }} className="mt-3 inline-flex items-center gap-2 btn-ghost rounded-full px-4 h-9 text-sm">
        <FileSpreadsheet size={15} className="text-brand" /> Download pricing matrix (Excel)
      </button>

      {/* Change admin password */}
      <div className="mt-3">
        <button data-testid="toggle-change-pw" onClick={() => setPw((p) => ({ ...p, open: !p.open }))}
          className="inline-flex items-center gap-2 btn-ghost rounded-full px-4 h-9 text-sm">
          <KeyRound size={15} className="text-brand" /> Change my password
        </button>
        {pw.open && (
          <div className="rud-card rounded-2xl p-4 mt-3 max-w-md space-y-2" data-testid="change-pw-form">
            <input data-testid="cp-current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <input data-testid="cp-next" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} placeholder="New password (min 8 chars)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <input data-testid="cp-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} placeholder="Confirm new password" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <button data-testid="cp-submit" onClick={changePassword} disabled={pw.busy} className="btn-primary rounded-full h-9 px-5 text-sm flex items-center gap-2 disabled:opacity-60">
              {pw.busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Update password
            </button>
          </div>
        )}
      </div>

      <div className="mt-6" data-testid="pending-members-section">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={16} className="text-brand" />
          <span className="text-sm font-medium">New members awaiting approval</span>
          <span className="text-xs text-sec">({pendingUsers.length})</span>
        </div>
        {pendingUsers.length === 0 ? (
          <div className="rounded-xl border border-rud bg-white/5 px-4 py-6 text-center text-sm text-sec" data-testid="no-pending-members">No one is waiting right now. 🎉</div>
        ) : (
          <div className="space-y-2">
            {pendingUsers.map((u) => (
              <div key={u.user_id} data-testid={`pending-user-${u.user_id}`} className="flex items-center gap-3 rounded-xl border border-rud bg-white/5 px-4 py-3">
                {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-brand/15 grid place-items-center text-brand text-sm">{(u.name || u.email || "?")[0]?.toUpperCase()}</div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{u.name || "New member"}</div>
                  <div className="text-[11px] text-sec truncate">{u.email}{u.referrer_name ? ` · referred by ${u.referrer_name}` : ""}</div>
                </div>
                <button data-testid={`approve-user-${u.user_id}`} onClick={() => approveUser(u)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Check size={13} /> Approve</button>
                <button data-testid={`reject-user-${u.user_id}`} onClick={() => rejectUser(u)} className="btn-ghost rounded-lg px-3 py-1.5 text-xs flex items-center gap-1 text-red-300"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 pt-6 border-t border-rud">
      <div className="flex items-center gap-2 text-brand mb-1"><UserPlus size={16} /> <span className="text-xs uppercase tracking-widest">Email allowlist (optional)</span></div>
      <p className="text-sec text-sm mt-1">Pre-approve people by email so they skip the waiting step entirely. {inviteOnly ? <span className="text-brand">Invite-only is ON.</span> : <span className="text-emerald-400">Signups are OPEN.</span>}</p></div>


      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="rounded-xl border border-rud bg-white/5 p-4"><div className="text-2xl font-serif" data-testid="stat-approved">{approved}</div><div className="text-xs text-sec">Approved members</div></div>
        <div className="rounded-xl border border-rud bg-white/5 p-4"><div className="text-2xl font-serif" data-testid="stat-pending">{pending}</div><div className="text-xs text-sec">On the waitlist</div></div>
      </div>

      <div className="mt-6 rounded-xl border border-brand/25 bg-brand/5 p-4">
        <div className="text-sm font-medium flex items-center gap-2 mb-2"><UserPlus size={16} className="text-brand" /> Invite someone</div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="invite-email-input" type="email" placeholder="member@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()} className="rud-input w-full rounded-lg pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <button data-testid="invite-add-btn" onClick={addEmail} disabled={busy} className="btn-primary rounded-lg px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Invite
          </button>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="text-xs text-sec mb-1.5">Or paste a whole list (commas, spaces or new lines):</div>
          <textarea data-testid="bulk-emails-input" value={bulk} onChange={(e) => setBulk(e.target.value)} rows={3}
            placeholder="ravi@example.com, priya@example.com&#10;anita@example.com" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-y" />
          <button data-testid="bulk-invite-btn" onClick={addBulk} disabled={busy || !bulk.trim()} className="btn-ghost rounded-lg px-4 py-2 text-sm mt-2 flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Invite everyone on the list
          </button>
        </div>
      </div>

      {inviters.length > 0 && (
        <div className="mt-8" data-testid="inviters-summary">
          <div className="text-sm font-medium flex items-center gap-2 mb-2"><UserPlus size={16} className="text-brand" /> Who invited whom</div>
          <div className="space-y-2">
            {inviters.map((iv) => (
              <div key={iv.inviter_email || iv.inviter} data-testid={`inviter-${iv.inviter_email || iv.inviter}`} className="flex items-center gap-3 rounded-lg border border-rud bg-white/5 px-4 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{iv.inviter}</div>
                  {iv.inviter_email && <div className="text-[11px] text-sec truncate">{iv.inviter_email}</div>}
                </div>
                <div className="text-right text-[11px] text-sec">
                  <span className="text-brand font-medium text-sm">{iv.total}</span> invited
                  <span className="mx-1">·</span>{iv.approved} approved<span className="mx-1">·</span>{iv.pending} waiting
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4" data-testid="vip-invite-box">
        <div className="text-sm font-medium flex items-center gap-2 mb-1"><Sparkles size={16} className="text-amber-400" /> Send a VIP invitation</div>
        <p className="text-[12px] text-sec mb-2">Approves the guest and sends the special personal My Kind letter (e.g. for Hon'ble PM Shri Narendra Modi Ji). Real delivery needs your Resend domain verified.</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
            <input data-testid="vip-email-input" type="email" placeholder="guest@example.com" value={vipEmail} onChange={(e) => setVipEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendVip()} className="rud-input w-full rounded-lg pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <button data-testid="vip-send-btn" onClick={sendVip} disabled={busy || !vipEmail.trim()} className="btn-primary rounded-lg px-4 text-sm flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Send VIP invite
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-8 mb-3">
        {[["", "All"], ["pending", "Waitlist"], ["approved", "Approved"]].map(([v, l]) => (
          <button key={v} data-testid={`filter-${v || "all"}`} onClick={() => setFilter(v)}
            className={`h-8 px-3 rounded-full text-xs transition-colors ${filter === v ? "bg-brand text-black font-medium" : "bg-white/5 text-sec border border-white/10 hover:text-[var(--rud-text)]"}`}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sec"><HeartLoader /></div>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sec text-sm">No entries yet.</p>
      ) : (
        <div className="space-y-2" data-testid="access-list">
          {items.map((it) => (
            <div key={it.email} data-testid={`access-row-${it.email}`} className="flex items-center gap-3 rounded-lg border border-rud bg-white/5 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{it.name ? <><b className="text-[var(--rud-text)]">{it.name}</b> · {it.email}</> : it.email}</div>
                <div className="text-[11px] text-sec flex items-center gap-1.5">
                  {it.status === "approved"
                    ? <><Check size={12} className="text-brand" /> Approved{it.invited_by ? ` · by ${it.invited_by}` : ""}</>
                    : <><Clock size={12} className="text-amber-400" /> Waitlist{it.inviter_name ? ` · invited by ${it.inviter_name}` : " · self-requested"}</>}
                </div>
              </div>
              {it.status !== "approved" && (
                <button data-testid={`approve-${it.email}`} onClick={() => approve(it.email)} className="btn-primary rounded-lg px-3 py-1.5 text-xs flex items-center gap-1"><Check size={13} /> Approve</button>
              )}
              <button data-testid={`remove-${it.email}`} onClick={() => remove(it.email)} className="text-sec hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
